import React, { forwardRef } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Chip, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { formatText, FORMAT_TYPE } from 'src/utils/era-package'
import { optionsMap, StatusChip, CHIP_MAP } from '.'
import { format, setMonth } from 'date-fns';

const columnHelper = createColumnHelper();
const handleExpansion = (e, row) => {
  e.preventDefault(); e.stopPropagation();
  row.getToggleExpandedHandler()(e)
}
const dateTimeCell = info => <Typography variant='body2' minWidth='7em'>
  {formatText(info.getValue(), FORMAT_TYPE.datetime)}
</Typography>
const dateCell = info => <Typography noWrap variant='body2'>
  {formatText(info.getValue(), FORMAT_TYPE.date)}
</Typography>

const ExpandAction = forwardRef(({ row, ...props }, ref) => (
  <IconButton
    onClick={e => handleExpansion(e, row)}
    {...props}
    ref={ref}
  >
    <FontAwesomeIcon
      icon={faChevronDown}
      rotation={row.getIsExpanded() ? 180 : 0}
      size='sm'
      width='1em'
    />
  </IconButton>
));

export const dailyStatementCols = [
  columnHelper.display({
    id: 'expand',
    cell: ({ row }) => row.getCanExpand()
      ? <ExpandAction row={row} /> : ''
  }),
  columnHelper.accessor('statementNumber', { header: 'Statement Number' }),
  columnHelper.accessor('isFinalized', {
    header: "Statement Type",
    cell: ({ row, ...info }) => {
      const val = row.original.statementType
      const option = optionsMap.statementType.find(x => x.value === val) || {}
      return <Stack spacing={.5} alignItems='flex-start'>
        <StatusChip finalized={info.getValue()} size='small' />
        <Typography variant='body2' noWrap>
          {option.description}
        </Typography>
      </Stack>
    }
  }),
  columnHelper.accessor('port', {
    header: 'Port',
  }),
  columnHelper.accessor('statementDate', {
    header: 'Statement Date',
    accessorFn: rowData => formatText(rowData.statementDate, FORMAT_TYPE.date, { out: 'raw' }),
    cell: dateCell,
    filterFn: 'dateRange'
  }),
  columnHelper.accessor('statementTotal', {
    header: 'Statement Total',
    cell: info => formatText(info.getValue(), FORMAT_TYPE.currency)
  }),
  columnHelper.accessor('authorizationTotal', {
    header: 'Auth Total',
    cell: info => formatText(info.getValue(), FORMAT_TYPE.currency)
  }),
  columnHelper.accessor('statementStatus', {
    header: 'Statement Status',
    filterFn: 'equalsString'
  }),
  columnHelper.accessor('authorizationStatus', {
    header: 'Auth Status',
    filterFn: 'equalsString'
  }),
  columnHelper.accessor('importerNumber', {
    header: 'Importer',
  }),
  columnHelper.accessor('filerCode', {
    header: 'Filer',
  }),
  columnHelper.accessor('receiveDate', {
    header: 'Receive Date',
    accessorFn: rowData => formatText(rowData.receiveDate, FORMAT_TYPE.date, { out: 'raw' }),
    cell: dateCell,
    filterFn: 'dateRange'
  }),
  columnHelper.accessor('authorizationDate', {
    header: 'Auth Date',
    accessorFn: rowData => formatText(rowData.authorizationDate, FORMAT_TYPE.date, { out: 'raw' }),
    cell: dateCell,
    filterFn: 'dateRange'
  }),
  columnHelper.accessor('relatedEntrySummary', {
    header: 'Entry Summary',
    filterFn: 'relatedEntrySummary',
    cell: () => null, // prevents rendering any content
    meta: { hidden: true }
  })
];

export const relatedEntryCols = [
  columnHelper.accessor('entryNumber', { header: 'Entry Number' }),
  columnHelper.accessor('importerNumber', { header: 'Importer Number' }),
  columnHelper.accessor('filerCode', { header: 'Filer Code' }),
  columnHelper.accessor('receiveDate', { header: 'Receive Date' }),
  columnHelper.accessor('authorizationDate', { header: 'Authorization Date' }),
]

const entryListCols = [
  { name: 'entryNumber', header: 'Entry Number' },
  { name: 'importerNumber', header: 'Importer Number' },
  { name: 'refNumber', header: 'Ref Number' },
  { name: 'estDuty', header: 'Estimated Duty', type: 'currency' },
  { name: 'estTax', header: 'Estimated Tax', type: 'currency' },
  { name: 'estCVD', header: 'Estimated CVD' },
  { name: 'estADA', header: 'Estimated ADA' },
  {
    name: 'userFeeInterest', header: 'User Fee/Interest', type: (val, rowData) => {
      const disp = formatText(val, FORMAT_TYPE.currency)
      if (rowData.reconIndicator === 'I')
        return disp + " / I"
      return disp
    }
  },
  { name: 'totalAmount', header: 'Total Amount', type: 'currency' },
  { name: 'dtySt', header: 'DTY ST' },
]

const pdsListCols = [
  { name: 'statementNumber', header: 'Statement Number' },
  { name: 'filer', header: 'Filer' },
  { name: 'importerNumber', header: 'Importer Number' },
  { name: 'statementDate', header: 'Statement Date', type: 'date' },
  { name: 'estDuty', header: 'Estimated Duty', type: 'currency' },
  { name: 'estTax', header: 'Estimated Tax', type: 'currency' },
  { name: 'estCVD', header: 'Estimated CVD', type: 'currency' },
  { name: 'estADD', header: 'Estimated ADD', type: 'currency' },
  { name: 'userFee', header: 'User Fee', type: 'currency' },
  { name: 'totalAmount', header: 'Total Amount', type: 'currency' },
]

const updateHistoryCols = [
  { name: 'totalAmountDueNew', header: 'Total Amount Due (New)', type: 'currency' },
  { name: 'totalAmountDueOld', header: 'Total Amount Due (Old)', type: 'currency' },
  { name: 'relatedEntryNumber', header: 'Related Entry Number' },
  { name: 'updatedTime', header: 'Updated Time', type: 'datetime' },
]

const deletedSummaryCols = [
  { name: 'dailyStatement', header: 'Daily Statement' },
  { name: 'entryNumber', header: 'Entry Number' },
  { name: 'deleteSource', header: 'Delete Source' },
  { name: 'filerCode', header: 'Filer Code' },
]

export const detailColumns = {
  entryList: entryListCols,
  dailyStatementUpdateHist: updateHistoryCols,
  entrySummaryDeletedHist: deletedSummaryCols,
  pdsList: pdsListCols,
  pmsUpdateHist: [
    { name: 'statementNumber', header: 'Statement Number' },
    ...updateHistoryCols
  ]
}

export const authListCols = [
  columnHelper.display({
    id: 'expand',
    cell: ({ row }) => row.getCanExpand()
      ? <Tooltip title={`${row.getIsExpanded ? 'Hide' : 'View'} response details`}>
        <ExpandAction row={row} />
      </Tooltip> : ''
  }),
  columnHelper.accessor('statementType', {
    header: "Statement Type",
    cell: info => {
      const val = info.getValue()
      const option = optionsMap.statementType.find(x => x.value === val) || {}
      if (option.short)
        return <Tooltip placement='top'
          title={option.description || option.value}>
          <span>{option.short}</span>
        </Tooltip>
      return option.description || option.value || val
    }
  }),
  columnHelper.accessor('payerUnitNumber', {
    header: "Payer's Unit Number",
  }),
  columnHelper.accessor('paymentAmount', {
    header: 'Payment Amount',
    cell: info => formatText(info.getValue(), FORMAT_TYPE.currency)
  }),
  columnHelper.accessor('isCancelPayment', {
    header: 'Cancel Payment',
    cell: info => info.getValue() ? 'Yes' : 'No'
  }),
  columnHelper.accessor('cancellationDate', {
    header: 'Cancellation Date',
    accessorFn: rowData => formatText(rowData.cancellationDate, FORMAT_TYPE.datetime, { out: 'raw' }),
    cell: dateTimeCell
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: info => {
      const disp = CHIP_MAP.get(info.getValue())
      if (!!disp)
        return <Typography variant='body2' color={disp.color + '.main'}>
          {disp.label}
        </Typography>
      return info.getValue() || null
    },
  }),
  columnHelper.accessor('filerCode', {
    header: 'Filer Code',
  }),
  columnHelper.accessor('userId', {
    header: 'User ID',
  }),
  columnHelper.accessor('submitTime', {
    header: 'Submit Time',
    accessorFn: rowData => formatText(rowData.submitTime, FORMAT_TYPE.datetime, { out: 'raw' }),
    cell: dateTimeCell
  }),
]

export const stmtUpdateCols = [
  columnHelper.display({
    id: 'expand',
    cell: ({ row }) => row.getCanExpand()
      ? <ExpandAction row={row} /> : ''
  }),
  columnHelper.accessor('entryNumber', {
    header: 'Entry Number',
    cell: info => <Typography variant='body2' noWrap>
      {info.getValue()}
    </Typography>
  }),
  columnHelper.accessor('paymentType', {
    header: 'Payment Type',
    cell: info => {
      const val = info.getValue()
      const desc = optionsMap.getText(val, 'paymentType')
      return <Tooltip title={desc} placement='top'>
        <Typography variant='body2' noWrap maxWidth='10em'>
          {info.getValue()} - {desc}
        </Typography>
      </Tooltip>
    }
  }),
  columnHelper.accessor('stmtMonth', {
    header: 'Statement Month',
    cell: info => {
      const date = setMonth(new Date(), Number(info.getValue()) - 1)
      return format(date, 'LLL')
    }
  }),
  columnHelper.accessor('stmtPrintDate', {
    header: 'Statement Print Date',
    accessorFn: rowData => formatText(rowData.stmtPrintDate, FORMAT_TYPE.date, { out: 'raw' }),
    cell: dateCell,
    filterFn: 'dateRange'
  }),
  columnHelper.accessor('clientBranch', {
    header: 'Client Branch',
  }),
  columnHelper.accessor('port', {
    header: 'Port',
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: info => {
      const disp = CHIP_MAP.get(info.getValue())
      if (!!disp)
        return <Typography variant='body2' color={disp.color + '.main'}>
          {disp.label}
        </Typography>
    },
  }),
  columnHelper.accessor('filerCode', {
    header: 'Filer Code',
  }),
  columnHelper.accessor('userId', {
    header: 'Submitted By',
  }),
  columnHelper.accessor('submitTime', {
    header: 'Submitted Time',
    accessorFn: rowData => formatText(rowData.submitTime, FORMAT_TYPE.datetime, { out: 'raw' }),
    cell: dateTimeCell,
    filterFn: 'dateRange'
  }),
  columnHelper.accessor('responseTime', {
    header: 'Response Received',
    accessorFn: rowData => formatText(rowData.responseTime, FORMAT_TYPE.datetime, { out: 'raw' }),
    cell: dateTimeCell,
    filterFn: 'dateRange'
  }),
]


const TotalsDisplay = ({ amount, ...chipProps }) => <Stack spacing={.5} alignItems='start'>
  <Chip size='small' variant='outlined'
    {...chipProps}
  />
  <Typography variant='body2'>
    {formatText(amount, FORMAT_TYPE.currency)}
  </Typography>
</Stack>

export const monthlyStmtCols = [
  columnHelper.accessor('pmsNumber', { header: 'Statement Number' }),
  columnHelper.accessor('isFinalized', {
    header: 'Status',
    cell: info => <StatusChip finalized={info.getValue()} size='small' />
  }),
  columnHelper.accessor('pmsPrintDate', {
    header: 'Print Date',
    accessorFn: rowData => formatText(rowData.pmsPrintDate, FORMAT_TYPE.date, { out: 'raw' }),
    cell: dateCell,
    filterFn: 'dateRange'
  }),
  columnHelper.accessor('pmsDueDate', {
    header: 'Due Date',
    accessorFn: rowData => formatText(rowData.pmsDueDate, FORMAT_TYPE.date, { out: 'raw' }),
    cell: dateCell,
    filterFn: 'dateRange'
  }),
  columnHelper.accessor('total', {
    header: 'Statement Totals',
    accessorFn: rowData => rowData.isFinalized
      ? rowData.finalTotal : rowData.authzTotal,
    cell: ({ row, ...info }) => {
      const isFinalized = row.original.isFinalized
      return <Stack spacing={2} direction='row'>
        <TotalsDisplay amount={row.original.pmsTotal}
          color='primary' label='Statement'
        />
        <Divider orientation='vertical' flexItem />
        <TotalsDisplay amount={info.getValue()}
          color={isFinalized ? 'success' : 'error'}
          label={isFinalized ? 'Finalized' : 'Authorized'}
        />
      </Stack>
    }
  }),
  columnHelper.accessor('difference', {
    header: 'Difference',
    cell: info => <Typography variant='body2'
      color={info.getValue() !== 0 ? 'error.main' : 'text.primary'}
    >
      {formatText(info.getValue(), FORMAT_TYPE.currency)}
    </Typography>
  }),
  columnHelper.accessor('pmsImporter', {
    header: 'Importer',
    cell: info => <Typography variant='body2' noWrap>{info.getValue()}</Typography>
  }),
  columnHelper.accessor('pmsFiler', { header: 'Filer' }),
  columnHelper.accessor('receiveDate', {
    header: 'Receive Date',
    accessorFn: rowData => formatText(rowData.receiveDate, FORMAT_TYPE.date, { out: 'raw' }),
    cell: dateCell,
    filterFn: 'dateRange'
  }),
];